<header
  class="fixed inset-x-0 top-0 z-50 shrink-0 bg-white"
  role="banner">

  <app-test-banner />

  <div class="relative border-b border-pale-gray bg-white">
  <div class="flex h-16 w-full items-center justify-between px-6">

    <!-- LEFT: brand anchor -->
    <div class="flex min-w-0 flex-1 items-center justify-start">
      <a
        routerLink="/"
        class="flex shrink-0 items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40"
        (click)="closeOverlays()">
        <img
          [src]="brandLogoSrc"
          alt="TimeFlexRent"
          class="h-9 w-auto max-w-[10.5rem] object-contain object-left sm:max-w-[12rem]" />
      </a>
    </div>

    <!-- CENTER: search (dead-center on md+) -->
    <div class="mx-auto hidden max-w-xl flex-1 items-center justify-center md:flex">
      <form class="w-full" (submit)="onSearchSubmit($event)" role="search">
        <label class="sr-only" for="header-search">Buscar espacios</label>
        <div
          class="group relative flex w-full items-center rounded-full border border-platinum-tint bg-cloud-mist py-2 pl-5 pr-4 text-sm text-slate-blue transition-all duration-200 hover:bg-pale-gray/70 focus-within:border-steel-gray focus-within:bg-white">
          <!-- HEADER_SEARCH_FILTERS: inject category tags / location filters here -->
          <lucide-icon
            name="search"
            [size]="16"
            class="pointer-events-none shrink-0 text-steel-gray transition-colors group-focus-within:text-steel-gray" />
          <input
            id="header-search"
            type="search"
            [value]="searchQuery()"
            (input)="searchQuery.set($any($event.target).value)"
            placeholder="Encuentra salas con más rapidez..."
            class="min-w-0 flex-1 border-0 bg-transparent pl-2.5 pr-0 text-sm text-slate-blue outline-none placeholder:text-steel-gray focus:ring-0" />
        </div>
      </form>
    </div>

    <!-- RIGHT: auth actions anchor -->
    <div class="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">

      <button
        type="button"
        class="hidden rounded-full px-3 py-2 text-sm font-semibold text-midnight-indigo transition hover:bg-cloud-mist focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40 md:inline-flex"
        (click)="onCreate()">
        {{ createLabel() }}
      </button>

      <button
        type="button"
        class="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-blue transition hover:bg-cloud-mist focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40 md:hidden"
        (click)="onCreate()"
        [attr.aria-label]="createLabel()">
        <lucide-icon name="plus" [size]="20" />
      </button>

      @if (!isLoggedIn()) {
        <button
          type="button"
          class="hidden rounded-full bg-action-blue px-5 py-2 text-sm font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40 sm:inline-flex"
          (click)="onLogin()">
          Iniciar sesión
        </button>

        <button
          type="button"
          class="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-blue transition hover:bg-cloud-mist focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40 md:hidden"
          (click)="toggleMobile()"
          [attr.aria-expanded]="mobileOpen()"
          aria-controls="header-mobile-menu"
          aria-label="Abrir menú">
          <lucide-icon [name]="mobileOpen() ? 'x' : 'menu'" [size]="20" />
        </button>
      } @else {
        <div class="relative">
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full ring-2 ring-pale-gray transition hover:ring-action-blue/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40"
            [class.bg-action-blue]="!authUser()?.photoURL"
            (click)="toggleProfile()"
            [attr.aria-expanded]="profileOpen()"
            aria-haspopup="menu"
            aria-label="Menú de cuenta">
            @if (authUser()?.photoURL) {
              <img
                [src]="authUser()!.photoURL!"
                [alt]="authUser()!.displayName || 'Cuenta'"
                class="h-full w-full object-cover" />
            } @else {
              <span class="text-xs font-bold text-snow-white">{{ userInitials() }}</span>
            }
          </button>

          @if (profileOpen()) {
            <button
              type="button"
              class="fixed inset-0 z-40 cursor-default bg-transparent"
              aria-label="Cerrar menú"
              (click)="closeOverlays()"></button>
            <div
              role="menu"
              class="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-2xl border border-pale-gray bg-white py-1 shadow-tfr-md">
              <div class="border-b border-pale-gray px-4 py-2.5">
                <p class="truncate text-sm font-semibold text-midnight-indigo">
                  {{ authUser()?.displayName || authUser()?.email }}
                </p>
              </div>
              @if (isAdmin()) {
                <button
                  type="button"
                  role="menuitem"
                  (click)="goToAdmin()"
                  class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-blue hover:bg-cloud-mist">
                  <lucide-icon name="shield-check" [size]="16" class="shrink-0 text-steel-gray" />
                  Panel admin
                </button>
              }
              @if (isOwner()) {
                <button
                  type="button"
                  role="menuitem"
                  (click)="goToMySpaces()"
                  class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-blue hover:bg-cloud-mist">
                  <lucide-icon name="building-2" [size]="16" class="shrink-0 text-steel-gray" />
                  Mis locales
                </button>
              }
              <button
                type="button"
                role="menuitem"
                (click)="goToAccount()"
                class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-blue hover:bg-cloud-mist">
                <lucide-icon name="user" [size]="16" class="shrink-0 text-steel-gray" />
                Mi cuenta
              </button>
              <button
                type="button"
                role="menuitem"
                (click)="logout()"
                class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-blue hover:bg-cloud-mist">
                <lucide-icon name="log-out" [size]="16" class="shrink-0 text-steel-gray" />
                Cerrar sesión
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>

  @if (mobileOpen()) {
    <button
      type="button"
      class="fixed inset-0 top-[calc(4rem+var(--tfr-banner-offset,0px))] z-40 bg-midnight-indigo/20 md:hidden"
      aria-label="Cerrar menú"
      (click)="closeOverlays()"></button>
    <nav
      id="header-mobile-menu"
      class="absolute inset-x-0 top-full z-50 border-b border-pale-gray bg-white px-6 py-3 shadow-tfr-sm md:hidden"
      aria-label="Navegación móvil">
      <form class="mb-3" (submit)="onSearchSubmit($event)" role="search">
        <label class="sr-only" for="header-search-mobile">Buscar espacios</label>
        <div
          class="group relative flex w-full items-center rounded-full border border-platinum-tint bg-cloud-mist transition-all duration-200 focus-within:border-steel-gray focus-within:bg-white">
          <lucide-icon
            name="search"
            [size]="16"
            class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-steel-gray" />
          <input
            id="header-search-mobile"
            type="search"
            [value]="searchQuery()"
            (input)="searchQuery.set($any($event.target).value)"
            placeholder="Encuentra salas con más rapidez..."
            class="w-full rounded-full border-0 bg-transparent py-2 pl-11 pr-4 text-sm text-slate-blue outline-none placeholder:text-steel-gray" />
        </div>
      </form>
      <button
        type="button"
        (click)="goToExplore()"
        class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-midnight-indigo hover:bg-cloud-mist">
        <lucide-icon name="search" [size]="18" class="text-steel-gray" />
        Explorar espacios
      </button>
      @if (!isLoggedIn()) {
        <button
          type="button"
          (click)="onLogin()"
          class="mt-1 flex w-full items-center justify-center rounded-full bg-action-blue px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-action-blue/40">
          Iniciar sesión
        </button>
      }
    </nav>
  }
  </div>
</header>
